import { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { COLOR_A, COLOR_B, PLATFORM_LABELS } from "../constants";
import { toCssVariables } from "../theme";
import { Btn, StatusBanner, useT } from "./ui";

function AppPlatformSelector({ apps, token, value, onChange, allowAllApps, accent, label }) {
  const [platforms, setPlatforms] = useState([]);
  const [platformError, setPlatformError] = useState("");

  useEffect(() => {
    let active = true;
    if (!value.appId) {
      setPlatforms([]);
      setPlatformError("");
      return () => { active = false; };
    }
    setPlatformError("");
    apiFetch(`/api/apps/${value.appId}/platforms`, { silent: true }, token)
      .then(data => { if (active) setPlatforms(data.platforms || []); })
      .catch(error => {
        if (!active) return;
        setPlatforms([]);
        setPlatformError(error.message || "플랫폼 설정을 불러오지 못했습니다.");
      });
    return () => { active = false; };
  }, [value.appId, token]);

  return (
    <div className="app-selector" style={{ "--slot-color": accent }}>
      {label && <span className="slot-badge">{label}</span>}
      <label className="field-group">
        <span className="field-group__label">앱</span>
        <select
          aria-label={`${label ? `${label} ` : ""}앱 선택`}
          value={value.allApps ? "__all" : (value.appId || "")}
          onChange={event => {
            const selected = event.target.value;
            if (selected === "__all") onChange({ appId: null, allApps: true, platform: "all" });
            else onChange({ appId: Number(selected) || null, allApps: false, platform: "" });
          }}
        >
          <option value="">앱 선택</option>
          {allowAllApps && <option value="__all">전체 앱</option>}
          {apps.map(app => <option key={app.id} value={app.id}>{app.app_name}</option>)}
        </select>
      </label>
      <label className="field-group">
        <span className="field-group__label">플랫폼</span>
        <select
          aria-label={`${label ? `${label} ` : ""}플랫폼 선택`}
          value={value.platform || ""}
          disabled={!value.appId}
          onChange={event => onChange({ ...value, platform: event.target.value })}
          title={platformError || undefined}
        >
          <option value="">플랫폼 선택</option>
          <option value="all">전체 플랫폼</option>
          {platformError && <option value="__error" disabled>플랫폼 설정 조회 실패</option>}
          {platforms.map(platform => (
            <option key={platform.platform} value={platform.platform}>
              {PLATFORM_LABELS[platform.platform] || platform.platform}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function Sidebar({ tabs, activeTab, onTabChange, user }) {
  return (
    <aside className="app-sidebar">
      <div className="brand-block">
        <div className="brand-mark" aria-hidden="true">M</div>
        <div>
          <strong>Monetize</strong>
          <span>Analytics workspace</span>
        </div>
      </div>
      <nav className="primary-nav" aria-label="주요 메뉴">
        <span className="primary-nav__label">WORKSPACE</span>
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? "is-active" : ""}
            onClick={() => onTabChange(tab.id)}
            aria-current={activeTab === tab.id ? "page" : undefined}
            data-testid={`nav-${tab.id}`}
          >
            <span className="nav-icon">{tab.icon}</span>
            <span className="nav-copy"><strong>{tab.label}</strong><small>{tab.description}</small></span>
          </button>
        ))}
      </nav>
      <div className="sidebar-profile">
        <span className="profile-avatar">{user.username.slice(0, 1).toUpperCase()}</span>
        <span><strong>{user.username}</strong><small>{user.role === "admin" ? "관리자" : "뷰어"}</small></span>
      </div>
    </aside>
  );
}

export default function DashboardShell({
  tabs, activeTab, onTabChange, user, apps, token,
  slotA, setSlotA, slotB, setSlotB, compareMode, setCompareMode,
  isDark, setIsDark, showLabels, setShowLabels, labelSize, setLabelSize,
  appsError, onRetryApps,
  children,
}) {
  const T = useT();
  const currentTab = tabs.find(tab => tab.id === activeTab) || tabs[0];
  const isAdmin = activeTab === "admin";

  return (
    <div className="app-shell" data-theme={isDark ? "dark" : "light"} style={toCssVariables(T)}>
      <Sidebar tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} user={user} />
      <div className="app-workspace">
        <header className="workspace-header">
          <div className="page-identity">
            <span>수익화 분석</span>
            <div><h1>{currentTab.label}</h1><p>{currentTab.description}</p></div>
          </div>
          <div className="view-actions">
            <button type="button" className={showLabels ? "is-active" : ""} onClick={() => setShowLabels(value => !value)} aria-pressed={showLabels}>
              <span aria-hidden="true">123</span> 값 표시
            </button>
            {showLabels && (
              <div className="label-size-group" aria-label="차트 값 글자 크기">
                {[["작게", 9], ["보통", 11], ["크게", 13]].map(([label, size]) => (
                  <button key={size} type="button" className={labelSize === size ? "is-active" : ""} onClick={() => setLabelSize(size)}>{label}</button>
                ))}
              </div>
            )}
            <button type="button" onClick={() => setIsDark(value => !value)} aria-label={T.toggleLabel}>
              <span aria-hidden="true">{T.toggleIcon}</span> {T.toggleLabel}
            </button>
          </div>
        </header>

        {appsError && (
          <div className="workspace-notice">
            <StatusBanner
              type="error"
              action={<Btn variant="ghost" onClick={onRetryApps}>다시 불러오기</Btn>}
            >
              앱 목록을 불러오지 못했습니다. 서버 실행 상태를 확인한 뒤 다시 시도해 주세요.
            </StatusBanner>
          </div>
        )}

        {!isAdmin && (
          <section className="context-toolbar" aria-label="분석 대상">
            <div className="context-toolbar__title"><span>분석 대상</span><strong>앱과 플랫폼을 선택하세요</strong></div>
            <AppPlatformSelector apps={apps} token={token} value={slotA} onChange={setSlotA} allowAllApps={activeTab === "overview"} accent={COLOR_A} label={compareMode ? "A" : null} />
            <button type="button" className={`compare-toggle${compareMode ? " is-active" : ""}`} onClick={() => setCompareMode(value => !value)} aria-pressed={compareMode}>
              <span aria-hidden="true">⇄</span> A/B 비교
            </button>
            {compareMode && <AppPlatformSelector apps={apps} token={token} value={slotB} onChange={setSlotB} allowAllApps={false} accent={COLOR_B} label="B" />}
          </section>
        )}

        <main className="workspace-content" data-testid="workspace-content">{children}</main>
      </div>
    </div>
  );
}
